import type { PriceConfidence, PriceFactor } from "../useItemTypeSettings";
import type {
  DeriveUnitPrice,
  EffectiveAmountOf,
  EffectiveInstallationInputsOf,
  InstallationAmountOf,
  IsManualComponent,
} from "./types";

// Display order of the factors; a group missing one still shows its line
export const PRICE_FACTORS: PriceFactor[] = ["material", "installation"];

const CONFIDENCE_RANK: Record<PriceConfidence, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

// Products and sums of cent values drift in floating point, so results are settled back to cents
const roundToCents = (value: number): number => Math.round(value * 100) / 100;

// A manual value is the most trusted one, so per property it always beats the suggestion
export const effectiveInstallationInputsOf: EffectiveInstallationInputsOf = (
  component,
) => ({
  hours: component.manualHours ?? component.suggestedHours,
  hourlyRate: component.manualHourlyRate ?? component.suggestedHourlyRate,
});

export const installationAmountOf: InstallationAmountOf = ({
  hours,
  hourlyRate,
}) =>
  hours === null || hourlyRate === null
    ? null
    : roundToCents(hours * hourlyRate);

export const effectiveAmountOf: EffectiveAmountOf = (component) => {
  if (component.factor === "material") {
    return component.manualAmount ?? component.suggestedAmount;
  }
  return installationAmountOf(effectiveInstallationInputsOf(component));
};

export const isManualComponent: IsManualComponent = (component) => {
  if (component.factor === "material") return component.manualAmount !== null;
  return component.manualHours !== null || component.manualHourlyRate !== null;
};

export const deriveUnitPrice: DeriveUnitPrice = ({ priceComponents }) => {
  let amount = 0;
  let hasAmount = false;
  let isManual = false;
  let confidence: PriceConfidence | null = null;

  for (const component of priceComponents) {
    const usedAmount = effectiveAmountOf(component);
    if (usedAmount === null) continue;
    hasAmount = true;
    amount += usedAmount;

    if (isManualComponent(component)) {
      isManual = true;
      continue;
    }
    const suggestedConfidence = component.suggestedConfidence;
    if (suggestedConfidence === null) continue;
    if (
      confidence === null ||
      CONFIDENCE_RANK[suggestedConfidence] < CONFIDENCE_RANK[confidence]
    ) {
      confidence = suggestedConfidence;
    }
  }

  if (!hasAmount) return null;
  return { amount: roundToCents(amount), confidence, isManual };
};
